import { css } from 'lit';
import { createSelector } from 'reselect';
import { QueueState, SignDto, UserPermissions } from '@typings/api';
import { AppAlert, NotificationErrorType } from '@typings/shared-types';
import ConfigCARSx from '@/config/ConfigCARSx';
import { AppSection, DMSView } from '@/constants';
import { isValidString } from '@/utils/utils';
import { resendSignMessage } from '@/api/dmsMessageApi';
import { signsSelector } from './dms/dms-selectors';
import userHasPermission from './user-permissions';
import store from './redux-store';
import { showMainBanner } from './redux-ui';

const selectAlerts = createSelector(signsSelector, (signs: SignDto[]): AppAlert[] => {
	const alerts: AppAlert[] = [];
	const userCanResendMessages = userHasPermission(UserPermissions.DMS_CAN_MANAGE_QUEUE);

	signs?.forEach((sign) => {
		const { id, number, currentMessageState, latestMessageLog } = sign;
		if (
			isValidString(sign.ipAddress) &&
			currentMessageState &&
			[QueueState.ERROR].includes(currentMessageState) &&
			latestMessageLog &&
			latestMessageLog.result === 'ERROR'
		) {
			const timeInMillis =
				latestMessageLog?.actionEnd ??
				latestMessageLog?.updated ??
				latestMessageLog?.created ??
				Date.now();

			const actions: AppAlert['actions'] = [
				{
					actionTitle: 'View queue',
					link: `${ConfigCARSx.Pages[AppSection.DMS].route}/${DMSView['sign-queue']}/${id}`,
				},
			];

			if (userCanResendMessages) {
				actions.push({
					actionTitle: 'Resend',
					icon: 'img/sprites.svg#icon-refresh',
					callbackFn: () => {
						void (async () => {
							const apiRequestReturn = await resendSignMessage(id);

							if (apiRequestReturn.response?.ok) {
								store.dispatch(
									showMainBanner(
										NotificationErrorType.SUCCESS,
										{
											title: `Sign message resent`,
										},
										2000,
									),
								);
							} else {
								store.dispatch(
									showMainBanner(
										NotificationErrorType.ERROR,
										{
											title: `Error resending message`,
											messages: apiRequestReturn.apiError ? [apiRequestReturn.apiError] : undefined,
										},
										2000,
									),
								);
							}
						})();
					},
				});
			}

			alerts.push({
				title: 'DMS message send error',
				category: 'ERROR',
				source: 'DMS module',
				icon: 'img/sprites.svg#icon-warning-fill',
				content: `Sign #${number} failed to post message -- ${currentMessageState ?? latestMessageLog?.result}`,
				timeInMillis,
				signId: id,
				actions,
			});
		}
	});

	return alerts;
});

export default selectAlerts;
